/**
 * Firebase initialization and auth configuration
 */
import { initializeApp, FirebaseApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, Auth } from 'firebase/auth';
import { getAnalytics, Analytics } from 'firebase/analytics';
import { config, validateFirebaseConfig } from '../config/environment';

let app: FirebaseApp | null = null;
let auth: Auth | null = null;
let analytics: Analytics | null = null;

/**
 * Initialize Firebase app
 * Validates config before initialization
 */
const initializeFirebase = (): FirebaseApp => {
  if (app) return app;

  if (!validateFirebaseConfig()) {
    throw new Error('Invalid Firebase configuration. Check environment variables.');
  }

  app = initializeApp(config.firebase);
  return app;
};

/**
 * Get Firebase Auth instance
 */
export const getFirebaseAuth = (): Auth => {
  if (auth) return auth;

  const firebaseApp = initializeFirebase();
  auth = getAuth(firebaseApp);
  return auth;
};

/**
 * Google Auth Provider configured for popup sign-in
 */
export const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: 'select_account', // Always show account selector
});

/**
 * Initialize Analytics (production only)
 */
export const initializeAnalytics = (): Analytics | null => {
  if (!config.features.enableAnalytics) return null;
  if (analytics) return analytics;

  try {
    const firebaseApp = initializeFirebase();
    analytics = getAnalytics(firebaseApp);
    return analytics;
  } catch (error) {
    console.warn('Failed to initialize analytics:', error);
    return null;
  }
};

// Export auth instance for direct use
export { auth };
